'use strict';

var middlewareHostHeader = require('@aws-sdk/middleware-host-header');
var middlewareLogger = require('@aws-sdk/middleware-logger');
var middlewareRecursionDetection = require('@aws-sdk/middleware-recursion-detection');
var middlewareUserAgent = require('@aws-sdk/middleware-user-agent');
var configResolver = require('@smithy/config-resolver');
var core = require('@smithy/core');
var schema = require('@smithy/core/schema');
var eventstreamSerdeConfigResolver = require('@smithy/eventstream-serde-config-resolver');
var middlewareContentLength = require('@smithy/middleware-content-length');
var middlewareEndpoint = require('@smithy/middleware-endpoint');
var middlewareRetry = require('@smithy/middleware-retry');
var smithyClient = require('@smithy/smithy-client');
var httpAuthSchemeProvider = require('./auth/httpAuthSchemeProvider');
var runtimeConfig = require('./runtimeConfig');
var regionConfigResolver = require('@aws-sdk/region-config-resolver');
var protocolHttp = require('@smithy/protocol-http');
var schemas_0 = require('./schemas/schemas_0');
var errors = require('./models/errors');
var CloudWatchLogsServiceException = require('./models/CloudWatchLogsServiceException');

const resolveClientEndpointParameters = (options) => {
    return Object.assign(options, {
        useDualstackEndpoint: options.useDualstackEndpoint ?? false,
        useFipsEndpoint: options.useFipsEndpoint ?? false,
        defaultSigningName: "logs",
    });
};
const commonParams = {
    UseFIPS: { type: "builtInParams", name: "useFipsEndpoint" },
    Endpoint: { type: "builtInParams", name: "endpoint" },
    Region: { type: "builtInParams", name: "region" },
    UseDualStack: { type: "builtInParams", name: "useDualstackEndpoint" },
};

const getHttpAuthExtensionConfiguration = (runtimeConfig) => {
    const _httpAuthSchemes = runtimeConfig.httpAuthSchemes;
    let _httpAuthSchemeProvider = runtimeConfig.httpAuthSchemeProvider;
    let _credentials = runtimeConfig.credentials;
    return {
        setHttpAuthScheme(httpAuthScheme) {
            const index = _httpAuthSchemes.findIndex((scheme) => scheme.schemeId === httpAuthScheme.schemeId);
            if (index === -1) {
                _httpAuthSchemes.push(httpAuthScheme);
            }
            else {
                _httpAuthSchemes.splice(index, 1, httpAuthScheme);
            }
        },
        httpAuthSchemes() {
            return _httpAuthSchemes;
        },
        setHttpAuthSchemeProvider(httpAuthSchemeProvider) {
            _httpAuthSchemeProvider = httpAuthSchemeProvider;
        },
        httpAuthSchemeProvider() {
            return _httpAuthSchemeProvider;
        },
        setCredentials(credentials) {
            _credentials = credentials;
        },
        credentials() {
            return _credentials;
        },
    };
};
const resolveHttpAuthRuntimeConfig = (config) => {
    return {
        httpAuthSchemes: config.httpAuthSchemes(),
        httpAuthSchemeProvider: config.httpAuthSchemeProvider(),
        credentials: config.credentials(),
    };
};

const resolveRuntimeExtensions = (runtimeConfig, extensions) => {
    const extensionConfiguration = Object.assign(regionConfigResolver.getAwsRegionExtensionConfiguration(runtimeConfig), smithyClient.getDefaultExtensionConfiguration(runtimeConfig), protocolHttp.getHttpHandlerExtensionConfiguration(runtimeConfig), getHttpAuthExtensionConfiguration(runtimeConfig));
    extensions.forEach((extension) => extension.configure(extensionConfiguration));
    return Object.assign(runtimeConfig, regionConfigResolver.resolveAwsRegionExtensionConfiguration(extensionConfiguration), smithyClient.resolveDefaultRuntimeConfig(extensionConfiguration), protocolHttp.resolveHttpHandlerRuntimeConfig(extensionConfiguration), resolveHttpAuthRuntimeConfig(extensionConfiguration));
};

class CloudWatchLogsClient extends smithyClient.Client {
    config;
    constructor(...[configuration]) {
        const _config_0 = runtimeConfig.getRuntimeConfig(configuration || {});
        super(_config_0);
        this.initConfig = _config_0;
        const _config_1 = resolveClientEndpointParameters(_config_0);
        const _config_2 = middlewareUserAgent.resolveUserAgentConfig(_config_1);
        const _config_3 = middlewareRetry.resolveRetryConfig(_config_2);
        const _config_4 = configResolver.resolveRegionConfig(_config_3);
        const _config_5 = middlewareHostHeader.resolveHostHeaderConfig(_config_4);
        const _config_6 = middlewareEndpoint.resolveEndpointConfig(_config_5);
        const _config_7 = eventstreamSerdeConfigResolver.resolveEventStreamSerdeConfig(_config_6);
        const _config_8 = httpAuthSchemeProvider.resolveHttpAuthSchemeConfig(_config_7);
        const _config_9 = resolveRuntimeExtensions(_config_8, configuration?.extensions || []);
        this.config = _config_9;
        this.middlewareStack.use(schema.getSchemaSerdePlugin(this.config));
        this.middlewareStack.use(middlewareUserAgent.getUserAgentPlugin(this.config));
        this.middlewareStack.use(middlewareRetry.getRetryPlugin(this.config));
        this.middlewareStack.use(middlewareContentLength.getContentLengthPlugin(this.config));
        this.middlewareStack.use(middlewareHostHeader.getHostHeaderPlugin(this.config));
        this.middlewareStack.use(middlewareLogger.getLoggerPlugin(this.config));
        this.middlewareStack.use(middlewareRecursionDetection.getRecursionDetectionPlugin(this.config));
        this.middlewareStack.use(core.getHttpAuthSchemeEndpointRuleSetPlugin(this.config, {
            httpAuthSchemeParametersProvider: httpAuthSchemeProvider.defaultCloudWatchLogsHttpAuthSchemeParametersProvider,
            identityProviderConfigProvider: async (config) => new core.DefaultIdentityProviderConfig({
                "aws.auth#sigv4": config.credentials,
            }),
        }));
        this.middlewareStack.use(core.getHttpSigningPlugin(this.config));
    }
    destroy() {
        super.destroy();
    }
}

class AssociateKmsKeyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "AssociateKmsKey", {})
    .n("CloudWatchLogsClient", "AssociateKmsKeyCommand")
    .sc(schemas_0.AssociateKmsKey$)
    .build() {
}

class AssociateSourceToS3TableIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "AssociateSourceToS3TableIntegration", {})
    .n("CloudWatchLogsClient", "AssociateSourceToS3TableIntegrationCommand")
    .sc(schemas_0.AssociateSourceToS3TableIntegration$)
    .build() {
}

class CancelExportTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CancelExportTask", {})
    .n("CloudWatchLogsClient", "CancelExportTaskCommand")
    .sc(schemas_0.CancelExportTask$)
    .build() {
}

class CancelImportTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CancelImportTask", {})
    .n("CloudWatchLogsClient", "CancelImportTaskCommand")
    .sc(schemas_0.CancelImportTask$)
    .build() {
}

class CreateDeliveryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateDelivery", {})
    .n("CloudWatchLogsClient", "CreateDeliveryCommand")
    .sc(schemas_0.CreateDelivery$)
    .build() {
}

class CreateExportTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateExportTask", {})
    .n("CloudWatchLogsClient", "CreateExportTaskCommand")
    .sc(schemas_0.CreateExportTask$)
    .build() {
}

class CreateImportTaskCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateImportTask", {})
    .n("CloudWatchLogsClient", "CreateImportTaskCommand")
    .sc(schemas_0.CreateImportTask$)
    .build() {
}

class CreateLogAnomalyDetectorCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateLogAnomalyDetector", {})
    .n("CloudWatchLogsClient", "CreateLogAnomalyDetectorCommand")
    .sc(schemas_0.CreateLogAnomalyDetector$)
    .build() {
}

class CreateLogGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateLogGroup", {})
    .n("CloudWatchLogsClient", "CreateLogGroupCommand")
    .sc(schemas_0.CreateLogGroup$)
    .build() {
}

class CreateLogStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateLogStream", {})
    .n("CloudWatchLogsClient", "CreateLogStreamCommand")
    .sc(schemas_0.CreateLogStream$)
    .build() {
}

class CreateLookupTableCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateLookupTable", {})
    .n("CloudWatchLogsClient", "CreateLookupTableCommand")
    .sc(schemas_0.CreateLookupTable$)
    .build() {
}

class CreateScheduledQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "CreateScheduledQuery", {})
    .n("CloudWatchLogsClient", "CreateScheduledQueryCommand")
    .sc(schemas_0.CreateScheduledQuery$)
    .build() {
}

class DeleteAccountPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteAccountPolicy", {})
    .n("CloudWatchLogsClient", "DeleteAccountPolicyCommand")
    .sc(schemas_0.DeleteAccountPolicy$)
    .build() {
}

class DeleteDataProtectionPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDataProtectionPolicy", {})
    .n("CloudWatchLogsClient", "DeleteDataProtectionPolicyCommand")
    .sc(schemas_0.DeleteDataProtectionPolicy$)
    .build() {
}

class DeleteDeliveryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDelivery", {})
    .n("CloudWatchLogsClient", "DeleteDeliveryCommand")
    .sc(schemas_0.DeleteDelivery$)
    .build() {
}

class DeleteDeliveryDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDeliveryDestination", {})
    .n("CloudWatchLogsClient", "DeleteDeliveryDestinationCommand")
    .sc(schemas_0.DeleteDeliveryDestination$)
    .build() {
}

class DeleteDeliveryDestinationPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDeliveryDestinationPolicy", {})
    .n("CloudWatchLogsClient", "DeleteDeliveryDestinationPolicyCommand")
    .sc(schemas_0.DeleteDeliveryDestinationPolicy$)
    .build() {
}

class DeleteDeliverySourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDeliverySource", {})
    .n("CloudWatchLogsClient", "DeleteDeliverySourceCommand")
    .sc(schemas_0.DeleteDeliverySource$)
    .build() {
}

class DeleteDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteDestination", {})
    .n("CloudWatchLogsClient", "DeleteDestinationCommand")
    .sc(schemas_0.DeleteDestination$)
    .build() {
}

class DeleteIndexPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteIndexPolicy", {})
    .n("CloudWatchLogsClient", "DeleteIndexPolicyCommand")
    .sc(schemas_0.DeleteIndexPolicy$)
    .build() {
}

class DeleteIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteIntegration", {})
    .n("CloudWatchLogsClient", "DeleteIntegrationCommand")
    .sc(schemas_0.DeleteIntegration$)
    .build() {
}

class DeleteLogAnomalyDetectorCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteLogAnomalyDetector", {})
    .n("CloudWatchLogsClient", "DeleteLogAnomalyDetectorCommand")
    .sc(schemas_0.DeleteLogAnomalyDetector$)
    .build() {
}

class DeleteLogGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteLogGroup", {})
    .n("CloudWatchLogsClient", "DeleteLogGroupCommand")
    .sc(schemas_0.DeleteLogGroup$)
    .build() {
}

class DeleteLogStreamCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteLogStream", {})
    .n("CloudWatchLogsClient", "DeleteLogStreamCommand")
    .sc(schemas_0.DeleteLogStream$)
    .build() {
}

class DeleteLookupTableCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteLookupTable", {})
    .n("CloudWatchLogsClient", "DeleteLookupTableCommand")
    .sc(schemas_0.DeleteLookupTable$)
    .build() {
}

class DeleteMetricFilterCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteMetricFilter", {})
    .n("CloudWatchLogsClient", "DeleteMetricFilterCommand")
    .sc(schemas_0.DeleteMetricFilter$)
    .build() {
}

class DeleteQueryDefinitionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteQueryDefinition", {})
    .n("CloudWatchLogsClient", "DeleteQueryDefinitionCommand")
    .sc(schemas_0.DeleteQueryDefinition$)
    .build() {
}

class DeleteResourcePolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteResourcePolicy", {})
    .n("CloudWatchLogsClient", "DeleteResourcePolicyCommand")
    .sc(schemas_0.DeleteResourcePolicy$)
    .build() {
}

class DeleteRetentionPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteRetentionPolicy", {})
    .n("CloudWatchLogsClient", "DeleteRetentionPolicyCommand")
    .sc(schemas_0.DeleteRetentionPolicy$)
    .build() {
}

class DeleteScheduledQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteScheduledQuery", {})
    .n("CloudWatchLogsClient", "DeleteScheduledQueryCommand")
    .sc(schemas_0.DeleteScheduledQuery$)
    .build() {
}

class DeleteSubscriptionFilterCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteSubscriptionFilter", {})
    .n("CloudWatchLogsClient", "DeleteSubscriptionFilterCommand")
    .sc(schemas_0.DeleteSubscriptionFilter$)
    .build() {
}

class DeleteTransformerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DeleteTransformer", {})
    .n("CloudWatchLogsClient", "DeleteTransformerCommand")
    .sc(schemas_0.DeleteTransformer$)
    .build() {
}

class DescribeAccountPoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeAccountPolicies", {})
    .n("CloudWatchLogsClient", "DescribeAccountPoliciesCommand")
    .sc(schemas_0.DescribeAccountPolicies$)
    .build() {
}

class DescribeConfigurationTemplatesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeConfigurationTemplates", {})
    .n("CloudWatchLogsClient", "DescribeConfigurationTemplatesCommand")
    .sc(schemas_0.DescribeConfigurationTemplates$)
    .build() {
}

class DescribeDeliveriesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeDeliveries", {})
    .n("CloudWatchLogsClient", "DescribeDeliveriesCommand")
    .sc(schemas_0.DescribeDeliveries$)
    .build() {
}

class DescribeDeliveryDestinationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeDeliveryDestinations", {})
    .n("CloudWatchLogsClient", "DescribeDeliveryDestinationsCommand")
    .sc(schemas_0.DescribeDeliveryDestinations$)
    .build() {
}

class DescribeDeliverySourcesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeDeliverySources", {})
    .n("CloudWatchLogsClient", "DescribeDeliverySourcesCommand")
    .sc(schemas_0.DescribeDeliverySources$)
    .build() {
}

class DescribeDestinationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeDestinations", {})
    .n("CloudWatchLogsClient", "DescribeDestinationsCommand")
    .sc(schemas_0.DescribeDestinations$)
    .build() {
}

class DescribeExportTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeExportTasks", {})
    .n("CloudWatchLogsClient", "DescribeExportTasksCommand")
    .sc(schemas_0.DescribeExportTasks$)
    .build() {
}

class DescribeFieldIndexesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeFieldIndexes", {})
    .n("CloudWatchLogsClient", "DescribeFieldIndexesCommand")
    .sc(schemas_0.DescribeFieldIndexes$)
    .build() {
}

class DescribeImportTaskBatchesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeImportTaskBatches", {})
    .n("CloudWatchLogsClient", "DescribeImportTaskBatchesCommand")
    .sc(schemas_0.DescribeImportTaskBatches$)
    .build() {
}

class DescribeImportTasksCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeImportTasks", {})
    .n("CloudWatchLogsClient", "DescribeImportTasksCommand")
    .sc(schemas_0.DescribeImportTasks$)
    .build() {
}

class DescribeIndexPoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeIndexPolicies", {})
    .n("CloudWatchLogsClient", "DescribeIndexPoliciesCommand")
    .sc(schemas_0.DescribeIndexPolicies$)
    .build() {
}

class DescribeLogGroupsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeLogGroups", {})
    .n("CloudWatchLogsClient", "DescribeLogGroupsCommand")
    .sc(schemas_0.DescribeLogGroups$)
    .build() {
}

class DescribeLogStreamsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeLogStreams", {})
    .n("CloudWatchLogsClient", "DescribeLogStreamsCommand")
    .sc(schemas_0.DescribeLogStreams$)
    .build() {
}

class DescribeLookupTablesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeLookupTables", {})
    .n("CloudWatchLogsClient", "DescribeLookupTablesCommand")
    .sc(schemas_0.DescribeLookupTables$)
    .build() {
}

class DescribeMetricFiltersCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeMetricFilters", {})
    .n("CloudWatchLogsClient", "DescribeMetricFiltersCommand")
    .sc(schemas_0.DescribeMetricFilters$)
    .build() {
}

class DescribeQueriesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeQueries", {})
    .n("CloudWatchLogsClient", "DescribeQueriesCommand")
    .sc(schemas_0.DescribeQueries$)
    .build() {
}

class DescribeQueryDefinitionsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeQueryDefinitions", {})
    .n("CloudWatchLogsClient", "DescribeQueryDefinitionsCommand")
    .sc(schemas_0.DescribeQueryDefinitions$)
    .build() {
}

class DescribeResourcePoliciesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeResourcePolicies", {})
    .n("CloudWatchLogsClient", "DescribeResourcePoliciesCommand")
    .sc(schemas_0.DescribeResourcePolicies$)
    .build() {
}

class DescribeSubscriptionFiltersCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DescribeSubscriptionFilters", {})
    .n("CloudWatchLogsClient", "DescribeSubscriptionFiltersCommand")
    .sc(schemas_0.DescribeSubscriptionFilters$)
    .build() {
}

class DisassociateKmsKeyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DisassociateKmsKey", {})
    .n("CloudWatchLogsClient", "DisassociateKmsKeyCommand")
    .sc(schemas_0.DisassociateKmsKey$)
    .build() {
}

class DisassociateSourceFromS3TableIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "DisassociateSourceFromS3TableIntegration", {})
    .n("CloudWatchLogsClient", "DisassociateSourceFromS3TableIntegrationCommand")
    .sc(schemas_0.DisassociateSourceFromS3TableIntegration$)
    .build() {
}

class FilterLogEventsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "FilterLogEvents", {})
    .n("CloudWatchLogsClient", "FilterLogEventsCommand")
    .sc(schemas_0.FilterLogEvents$)
    .build() {
}

class GetDataProtectionPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetDataProtectionPolicy", {})
    .n("CloudWatchLogsClient", "GetDataProtectionPolicyCommand")
    .sc(schemas_0.GetDataProtectionPolicy$)
    .build() {
}

class GetDeliveryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetDelivery", {})
    .n("CloudWatchLogsClient", "GetDeliveryCommand")
    .sc(schemas_0.GetDelivery$)
    .build() {
}

class GetDeliveryDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetDeliveryDestination", {})
    .n("CloudWatchLogsClient", "GetDeliveryDestinationCommand")
    .sc(schemas_0.GetDeliveryDestination$)
    .build() {
}

class GetDeliveryDestinationPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetDeliveryDestinationPolicy", {})
    .n("CloudWatchLogsClient", "GetDeliveryDestinationPolicyCommand")
    .sc(schemas_0.GetDeliveryDestinationPolicy$)
    .build() {
}

class GetDeliverySourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetDeliverySource", {})
    .n("CloudWatchLogsClient", "GetDeliverySourceCommand")
    .sc(schemas_0.GetDeliverySource$)
    .build() {
}

class GetIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetIntegration", {})
    .n("CloudWatchLogsClient", "GetIntegrationCommand")
    .sc(schemas_0.GetIntegration$)
    .build() {
}

class GetLogAnomalyDetectorCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogAnomalyDetector", {})
    .n("CloudWatchLogsClient", "GetLogAnomalyDetectorCommand")
    .sc(schemas_0.GetLogAnomalyDetector$)
    .build() {
}

class GetLogEventsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogEvents", {})
    .n("CloudWatchLogsClient", "GetLogEventsCommand")
    .sc(schemas_0.GetLogEvents$)
    .build() {
}

class GetLogFieldsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogFields", {})
    .n("CloudWatchLogsClient", "GetLogFieldsCommand")
    .sc(schemas_0.GetLogFields$)
    .build() {
}

class GetLogGroupFieldsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogGroupFields", {})
    .n("CloudWatchLogsClient", "GetLogGroupFieldsCommand")
    .sc(schemas_0.GetLogGroupFields$)
    .build() {
}

class GetLogObjectCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogObject", {
    eventStream: {
        output: true,
    },
})
    .n("CloudWatchLogsClient", "GetLogObjectCommand")
    .sc(schemas_0.GetLogObject$)
    .build() {
}

class GetLogRecordCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLogRecord", {})
    .n("CloudWatchLogsClient", "GetLogRecordCommand")
    .sc(schemas_0.GetLogRecord$)
    .build() {
}

class GetLookupTableCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetLookupTable", {})
    .n("CloudWatchLogsClient", "GetLookupTableCommand")
    .sc(schemas_0.GetLookupTable$)
    .build() {
}

class GetQueryResultsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetQueryResults", {})
    .n("CloudWatchLogsClient", "GetQueryResultsCommand")
    .sc(schemas_0.GetQueryResults$)
    .build() {
}

class GetScheduledQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetScheduledQuery", {})
    .n("CloudWatchLogsClient", "GetScheduledQueryCommand")
    .sc(schemas_0.GetScheduledQuery$)
    .build() {
}

class GetScheduledQueryHistoryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetScheduledQueryHistory", {})
    .n("CloudWatchLogsClient", "GetScheduledQueryHistoryCommand")
    .sc(schemas_0.GetScheduledQueryHistory$)
    .build() {
}

class GetTransformerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "GetTransformer", {})
    .n("CloudWatchLogsClient", "GetTransformerCommand")
    .sc(schemas_0.GetTransformer$)
    .build() {
}

class ListAggregateLogGroupSummariesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListAggregateLogGroupSummaries", {})
    .n("CloudWatchLogsClient", "ListAggregateLogGroupSummariesCommand")
    .sc(schemas_0.ListAggregateLogGroupSummaries$)
    .build() {
}

class ListAnomaliesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListAnomalies", {})
    .n("CloudWatchLogsClient", "ListAnomaliesCommand")
    .sc(schemas_0.ListAnomalies$)
    .build() {
}

class ListIntegrationsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListIntegrations", {})
    .n("CloudWatchLogsClient", "ListIntegrationsCommand")
    .sc(schemas_0.ListIntegrations$)
    .build() {
}

class ListLogAnomalyDetectorsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListLogAnomalyDetectors", {})
    .n("CloudWatchLogsClient", "ListLogAnomalyDetectorsCommand")
    .sc(schemas_0.ListLogAnomalyDetectors$)
    .build() {
}

class ListLogGroupsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListLogGroups", {})
    .n("CloudWatchLogsClient", "ListLogGroupsCommand")
    .sc(schemas_0.ListLogGroups$)
    .build() {
}

class ListLogGroupsForQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListLogGroupsForQuery", {})
    .n("CloudWatchLogsClient", "ListLogGroupsForQueryCommand")
    .sc(schemas_0.ListLogGroupsForQuery$)
    .build() {
}

class ListScheduledQueriesCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListScheduledQueries", {})
    .n("CloudWatchLogsClient", "ListScheduledQueriesCommand")
    .sc(schemas_0.ListScheduledQueries$)
    .build() {
}

class ListSourcesForS3TableIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListSourcesForS3TableIntegration", {})
    .n("CloudWatchLogsClient", "ListSourcesForS3TableIntegrationCommand")
    .sc(schemas_0.ListSourcesForS3TableIntegration$)
    .build() {
}

class ListTagsForResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListTagsForResource", {})
    .n("CloudWatchLogsClient", "ListTagsForResourceCommand")
    .sc(schemas_0.ListTagsForResource$)
    .build() {
}

class ListTagsLogGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "ListTagsLogGroup", {})
    .n("CloudWatchLogsClient", "ListTagsLogGroupCommand")
    .sc(schemas_0.ListTagsLogGroup$)
    .build() {
}

class PutAccountPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutAccountPolicy", {})
    .n("CloudWatchLogsClient", "PutAccountPolicyCommand")
    .sc(schemas_0.PutAccountPolicy$)
    .build() {
}

class PutBearerTokenAuthenticationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutBearerTokenAuthentication", {})
    .n("CloudWatchLogsClient", "PutBearerTokenAuthenticationCommand")
    .sc(schemas_0.PutBearerTokenAuthentication$)
    .build() {
}

class PutDataProtectionPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDataProtectionPolicy", {})
    .n("CloudWatchLogsClient", "PutDataProtectionPolicyCommand")
    .sc(schemas_0.PutDataProtectionPolicy$)
    .build() {
}

class PutDeliveryDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDeliveryDestination", {})
    .n("CloudWatchLogsClient", "PutDeliveryDestinationCommand")
    .sc(schemas_0.PutDeliveryDestination$)
    .build() {
}

class PutDeliveryDestinationPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDeliveryDestinationPolicy", {})
    .n("CloudWatchLogsClient", "PutDeliveryDestinationPolicyCommand")
    .sc(schemas_0.PutDeliveryDestinationPolicy$)
    .build() {
}

class PutDeliverySourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDeliverySource", {})
    .n("CloudWatchLogsClient", "PutDeliverySourceCommand")
    .sc(schemas_0.PutDeliverySource$)
    .build() {
}

class PutDestinationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDestination", {})
    .n("CloudWatchLogsClient", "PutDestinationCommand")
    .sc(schemas_0.PutDestination$)
    .build() {
}

class PutDestinationPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutDestinationPolicy", {})
    .n("CloudWatchLogsClient", "PutDestinationPolicyCommand")
    .sc(schemas_0.PutDestinationPolicy$)
    .build() {
}

class PutIndexPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutIndexPolicy", {})
    .n("CloudWatchLogsClient", "PutIndexPolicyCommand")
    .sc(schemas_0.PutIndexPolicy$)
    .build() {
}

class PutIntegrationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutIntegration", {})
    .n("CloudWatchLogsClient", "PutIntegrationCommand")
    .sc(schemas_0.PutIntegration$)
    .build() {
}

class PutLogEventsCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutLogEvents", {})
    .n("CloudWatchLogsClient", "PutLogEventsCommand")
    .sc(schemas_0.PutLogEvents$)
    .build() {
}

class PutLogGroupDeletionProtectionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutLogGroupDeletionProtection", {})
    .n("CloudWatchLogsClient", "PutLogGroupDeletionProtectionCommand")
    .sc(schemas_0.PutLogGroupDeletionProtection$)
    .build() {
}

class PutMetricFilterCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutMetricFilter", {})
    .n("CloudWatchLogsClient", "PutMetricFilterCommand")
    .sc(schemas_0.PutMetricFilter$)
    .build() {
}

class PutQueryDefinitionCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutQueryDefinition", {})
    .n("CloudWatchLogsClient", "PutQueryDefinitionCommand")
    .sc(schemas_0.PutQueryDefinition$)
    .build() {
}

class PutResourcePolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutResourcePolicy", {})
    .n("CloudWatchLogsClient", "PutResourcePolicyCommand")
    .sc(schemas_0.PutResourcePolicy$)
    .build() {
}

class PutRetentionPolicyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutRetentionPolicy", {})
    .n("CloudWatchLogsClient", "PutRetentionPolicyCommand")
    .sc(schemas_0.PutRetentionPolicy$)
    .build() {
}

class PutSubscriptionFilterCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutSubscriptionFilter", {})
    .n("CloudWatchLogsClient", "PutSubscriptionFilterCommand")
    .sc(schemas_0.PutSubscriptionFilter$)
    .build() {
}

class PutTransformerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "PutTransformer", {})
    .n("CloudWatchLogsClient", "PutTransformerCommand")
    .sc(schemas_0.PutTransformer$)
    .build() {
}

class StartLiveTailCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "StartLiveTail", {
    eventStream: {
        output: true,
    },
})
    .n("CloudWatchLogsClient", "StartLiveTailCommand")
    .sc(schemas_0.StartLiveTail$)
    .build() {
}

class StartQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "StartQuery", {})
    .n("CloudWatchLogsClient", "StartQueryCommand")
    .sc(schemas_0.StartQuery$)
    .build() {
}

class StopQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "StopQuery", {})
    .n("CloudWatchLogsClient", "StopQueryCommand")
    .sc(schemas_0.StopQuery$)
    .build() {
}

class TagLogGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "TagLogGroup", {})
    .n("CloudWatchLogsClient", "TagLogGroupCommand")
    .sc(schemas_0.TagLogGroup$)
    .build() {
}

class TagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "TagResource", {})
    .n("CloudWatchLogsClient", "TagResourceCommand")
    .sc(schemas_0.TagResource$)
    .build() {
}

class TestMetricFilterCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "TestMetricFilter", {})
    .n("CloudWatchLogsClient", "TestMetricFilterCommand")
    .sc(schemas_0.TestMetricFilter$)
    .build() {
}

class TestTransformerCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "TestTransformer", {})
    .n("CloudWatchLogsClient", "TestTransformerCommand")
    .sc(schemas_0.TestTransformer$)
    .build() {
}

class UntagLogGroupCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UntagLogGroup", {})
    .n("CloudWatchLogsClient", "UntagLogGroupCommand")
    .sc(schemas_0.UntagLogGroup$)
    .build() {
}

class UntagResourceCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UntagResource", {})
    .n("CloudWatchLogsClient", "UntagResourceCommand")
    .sc(schemas_0.UntagResource$)
    .build() {
}

class UpdateAnomalyCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UpdateAnomaly", {})
    .n("CloudWatchLogsClient", "UpdateAnomalyCommand")
    .sc(schemas_0.UpdateAnomaly$)
    .build() {
}

class UpdateDeliveryConfigurationCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UpdateDeliveryConfiguration", {})
    .n("CloudWatchLogsClient", "UpdateDeliveryConfigurationCommand")
    .sc(schemas_0.UpdateDeliveryConfiguration$)
    .build() {
}

class UpdateLogAnomalyDetectorCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UpdateLogAnomalyDetector", {})
    .n("CloudWatchLogsClient", "UpdateLogAnomalyDetectorCommand")
    .sc(schemas_0.UpdateLogAnomalyDetector$)
    .build() {
}

class UpdateLookupTableCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UpdateLookupTable", {})
    .n("CloudWatchLogsClient", "UpdateLookupTableCommand")
    .sc(schemas_0.UpdateLookupTable$)
    .build() {
}

class UpdateScheduledQueryCommand extends smithyClient.Command
    .classBuilder()
    .ep(commonParams)
    .m(function (Command, cs, config, o) {
    return [middlewareEndpoint.getEndpointPlugin(config, Command.getEndpointParameterInstructions())];
})
    .s("Logs_20140328", "UpdateScheduledQuery", {})
    .n("CloudWatchLogsClient", "UpdateScheduledQueryCommand")
    .sc(schemas_0.UpdateScheduledQuery$)
    .build() {
}

const paginateDescribeConfigurationTemplates = core.createPaginator(CloudWatchLogsClient, DescribeConfigurationTemplatesCommand, "nextToken", "nextToken", "limit");

const paginateDescribeDeliveries = core.createPaginator(CloudWatchLogsClient, DescribeDeliveriesCommand, "nextToken", "nextToken", "limit");

const paginateDescribeDeliveryDestinations = core.createPaginator(CloudWatchLogsClient, DescribeDeliveryDestinationsCommand, "nextToken", "nextToken", "limit");

const paginateDescribeDeliverySources = core.createPaginator(CloudWatchLogsClient, DescribeDeliverySourcesCommand, "nextToken", "nextToken", "limit");

const paginateDescribeDestinations = core.createPaginator(CloudWatchLogsClient, DescribeDestinationsCommand, "nextToken", "nextToken", "limit");

const paginateDescribeLogGroups = core.createPaginator(CloudWatchLogsClient, DescribeLogGroupsCommand, "nextToken", "nextToken", "limit");

const paginateDescribeLogStreams = core.createPaginator(CloudWatchLogsClient, DescribeLogStreamsCommand, "nextToken", "nextToken", "limit");

const paginateDescribeMetricFilters = core.createPaginator(CloudWatchLogsClient, DescribeMetricFiltersCommand, "nextToken", "nextToken", "limit");

const paginateDescribeSubscriptionFilters = core.createPaginator(CloudWatchLogsClient, DescribeSubscriptionFiltersCommand, "nextToken", "nextToken", "limit");

const paginateFilterLogEvents = core.createPaginator(CloudWatchLogsClient, FilterLogEventsCommand, "nextToken", "nextToken", "limit");

const paginateGetLogEvents = core.createPaginator(CloudWatchLogsClient, GetLogEventsCommand, "nextToken", "nextForwardToken", "limit");

const paginateGetScheduledQueryHistory = core.createPaginator(CloudWatchLogsClient, GetScheduledQueryHistoryCommand, "nextToken", "nextToken", "maxResults");

const paginateListAggregateLogGroupSummaries = core.createPaginator(CloudWatchLogsClient, ListAggregateLogGroupSummariesCommand, "nextToken", "nextToken", "limit");

const paginateListAnomalies = core.createPaginator(CloudWatchLogsClient, ListAnomaliesCommand, "nextToken", "nextToken", "limit");

const paginateListLogAnomalyDetectors = core.createPaginator(CloudWatchLogsClient, ListLogAnomalyDetectorsCommand, "nextToken", "nextToken", "limit");

const paginateListLogGroupsForQuery = core.createPaginator(CloudWatchLogsClient, ListLogGroupsForQueryCommand, "nextToken", "nextToken", "maxResults");

const paginateListScheduledQueries = core.createPaginator(CloudWatchLogsClient, ListScheduledQueriesCommand, "nextToken", "nextToken", "maxResults");

const paginateListSourcesForS3TableIntegration = core.createPaginator(CloudWatchLogsClient, ListSourcesForS3TableIntegrationCommand, "nextToken", "nextToken", "maxResults");

const commands = {
    AssociateKmsKeyCommand,
    AssociateSourceToS3TableIntegrationCommand,
    CancelExportTaskCommand,
    CancelImportTaskCommand,
    CreateDeliveryCommand,
    CreateExportTaskCommand,
    CreateImportTaskCommand,
    CreateLogAnomalyDetectorCommand,
    CreateLogGroupCommand,
    CreateLogStreamCommand,
    CreateLookupTableCommand,
    CreateScheduledQueryCommand,
    DeleteAccountPolicyCommand,
    DeleteDataProtectionPolicyCommand,
    DeleteDeliveryCommand,
    DeleteDeliveryDestinationCommand,
    DeleteDeliveryDestinationPolicyCommand,
    DeleteDeliverySourceCommand,
    DeleteDestinationCommand,
    DeleteIndexPolicyCommand,
    DeleteIntegrationCommand,
    DeleteLogAnomalyDetectorCommand,
    DeleteLogGroupCommand,
    DeleteLogStreamCommand,
    DeleteLookupTableCommand,
    DeleteMetricFilterCommand,
    DeleteQueryDefinitionCommand,
    DeleteResourcePolicyCommand,
    DeleteRetentionPolicyCommand,
    DeleteScheduledQueryCommand,
    DeleteSubscriptionFilterCommand,
    DeleteTransformerCommand,
    DescribeAccountPoliciesCommand,
    DescribeConfigurationTemplatesCommand,
    DescribeDeliveriesCommand,
    DescribeDeliveryDestinationsCommand,
    DescribeDeliverySourcesCommand,
    DescribeDestinationsCommand,
    DescribeExportTasksCommand,
    DescribeFieldIndexesCommand,
    DescribeImportTaskBatchesCommand,
    DescribeImportTasksCommand,
    DescribeIndexPoliciesCommand,
    DescribeLogGroupsCommand,
    DescribeLogStreamsCommand,
    DescribeLookupTablesCommand,
    DescribeMetricFiltersCommand,
    DescribeQueriesCommand,
    DescribeQueryDefinitionsCommand,
    DescribeResourcePoliciesCommand,
    DescribeSubscriptionFiltersCommand,
    DisassociateKmsKeyCommand,
    DisassociateSourceFromS3TableIntegrationCommand,
    FilterLogEventsCommand,
    GetDataProtectionPolicyCommand,
    GetDeliveryCommand,
    GetDeliveryDestinationCommand,
    GetDeliveryDestinationPolicyCommand,
    GetDeliverySourceCommand,
    GetIntegrationCommand,
    GetLogAnomalyDetectorCommand,
    GetLogEventsCommand,
    GetLogFieldsCommand,
    GetLogGroupFieldsCommand,
    GetLogObjectCommand,
    GetLogRecordCommand,
    GetLookupTableCommand,
    GetQueryResultsCommand,
    GetScheduledQueryCommand,
    GetScheduledQueryHistoryCommand,
    GetTransformerCommand,
    ListAggregateLogGroupSummariesCommand,
    ListAnomaliesCommand,
    ListIntegrationsCommand,
    ListLogAnomalyDetectorsCommand,
    ListLogGroupsCommand,
    ListLogGroupsForQueryCommand,
    ListScheduledQueriesCommand,
    ListSourcesForS3TableIntegrationCommand,
    ListTagsForResourceCommand,
    ListTagsLogGroupCommand,
    PutAccountPolicyCommand,
    PutBearerTokenAuthenticationCommand,
    PutDataProtectionPolicyCommand,
    PutDeliveryDestinationCommand,
    PutDeliveryDestinationPolicyCommand,
    PutDeliverySourceCommand,
    PutDestinationCommand,
    PutDestinationPolicyCommand,
    PutIndexPolicyCommand,
    PutIntegrationCommand,
    PutLogEventsCommand,
    PutLogGroupDeletionProtectionCommand,
    PutMetricFilterCommand,
    PutQueryDefinitionCommand,
    PutResourcePolicyCommand,
    PutRetentionPolicyCommand,
    PutSubscriptionFilterCommand,
    PutTransformerCommand,
    StartLiveTailCommand,
    StartQueryCommand,
    StopQueryCommand,
    TagLogGroupCommand,
    TagResourceCommand,
    TestMetricFilterCommand,
    TestTransformerCommand,
    UntagLogGroupCommand,
    UntagResourceCommand,
    UpdateAnomalyCommand,
    UpdateDeliveryConfigurationCommand,
    UpdateLogAnomalyDetectorCommand,
    UpdateLookupTableCommand,
    UpdateScheduledQueryCommand,
};
const paginators = {
    paginateDescribeConfigurationTemplates,
    paginateDescribeDeliveries,
    paginateDescribeDeliveryDestinations,
    paginateDescribeDeliverySources,
    paginateDescribeDestinations,
    paginateDescribeLogGroups,
    paginateDescribeLogStreams,
    paginateDescribeMetricFilters,
    paginateDescribeSubscriptionFilters,
    paginateFilterLogEvents,
    paginateGetLogEvents,
    paginateGetScheduledQueryHistory,
    paginateListAggregateLogGroupSummaries,
    paginateListAnomalies,
    paginateListLogAnomalyDetectors,
    paginateListLogGroupsForQuery,
    paginateListScheduledQueries,
    paginateListSourcesForS3TableIntegration,
};
class CloudWatchLogs extends CloudWatchLogsClient {
}
smithyClient.createAggregatedClient(commands, CloudWatchLogs, { paginators });

const PolicyType = {
    DATA_PROTECTION_POLICY: "DATA_PROTECTION_POLICY",
    FIELD_INDEX_POLICY: "FIELD_INDEX_POLICY",
    METRIC_EXTRACTION_POLICY: "METRIC_EXTRACTION_POLICY",
    SUBSCRIPTION_FILTER_POLICY: "SUBSCRIPTION_FILTER_POLICY",
    TRANSFORMER_POLICY: "TRANSFORMER_POLICY",
};
const Scope = {
    ALL: "ALL",
};
const ActionStatus = {
    CLIENT_ERROR: "CLIENT_ERROR",
    COMPLETE: "COMPLETE",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
};
const State = {
    Active: "Active",
    Baseline: "Baseline",
    Suppressed: "Suppressed",
};
const AnomalyDetectorStatus = {
    ANALYZING: "ANALYZING",
    DELETED: "DELETED",
    FAILED: "FAILED",
    INITIALIZING: "INITIALIZING",
    PAUSED: "PAUSED",
    TRAINING: "TRAINING",
};
const EvaluationFrequency = {
    FIFTEEN_MIN: "FIFTEEN_MIN",
    FIVE_MIN: "FIVE_MIN",
    ONE_HOUR: "ONE_HOUR",
    ONE_MIN: "ONE_MIN",
    TEN_MIN: "TEN_MIN",
    THIRTY_MIN: "THIRTY_MIN",
};
const ImportStatus = {
    CANCELLED: "CANCELLED",
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    IN_PROGRESS: "IN_PROGRESS",
};
const OutputFormat = {
    JSON: "json",
    PARQUET: "parquet",
    PLAIN: "plain",
    RAW: "raw",
    W3C: "w3c",
};
const DeliveryDestinationType = {
    CWL: "CWL",
    FH: "FH",
    S3: "S3",
    XRAY: "XRAY",
};
const DeliverySourceConfigurationSchemaValueType = {
    BOOLEAN: "boolean",
    DOUBLE: "double",
    INT: "int",
    LONG: "long",
    STRING: "string",
};
const LogGroupClass = {
    DELIVERY: "DELIVERY",
    INFREQUENT_ACCESS: "INFREQUENT_ACCESS",
    STANDARD: "STANDARD",
};
const QueryLanguage = {
    CWLI: "CWLI",
    PPL: "PPL",
    SQL: "SQL",
};
const ScheduledQueryState = {
    DISABLED: "DISABLED",
    ENABLED: "ENABLED",
};
const DataProtectionStatus = {
    ACTIVATED: "ACTIVATED",
    ARCHIVED: "ARCHIVED",
    DELETED: "DELETED",
    DISABLED: "DISABLED",
};
const DeliverySourceStatus = {
    ACTIVE: "ACTIVE",
    INACTIVE: "INACTIVE",
};
const DeliverySourceStatusReason = {
    RESOURCE_DELETED: "RESOURCE_DELETED",
};
const ExportTaskStatusCode = {
    CANCELLED: "CANCELLED",
    COMPLETED: "COMPLETED",
    FAILED: "FAILED",
    PENDING: "PENDING",
    PENDING_CANCEL: "PENDING_CANCEL",
    RUNNING: "RUNNING",
};
const IndexType = {
    FACET: "FACET",
    FIELD_INDEX: "FIELD_INDEX",
};
const IndexSource = {
    ACCOUNT: "ACCOUNT",
    LOG_GROUP: "LOG_GROUP",
};
const InheritedProperty = {
    ACCOUNT_DATA_PROTECTION: "ACCOUNT_DATA_PROTECTION",
};
const OrderBy = {
    LastEventTime: "LastEventTime",
    LogStreamName: "LogStreamName",
};
const StandardUnit = {
    Bits: "Bits",
    BitsSecond: "Bits/Second",
    Bytes: "Bytes",
    BytesSecond: "Bytes/Second",
    Count: "Count",
    CountSecond: "Count/Second",
    Gigabits: "Gigabits",
    GigabitsSecond: "Gigabits/Second",
    Gigabytes: "Gigabytes",
    GigabytesSecond: "Gigabytes/Second",
    Kilobits: "Kilobits",
    KilobitsSecond: "Kilobits/Second",
    Kilobytes: "Kilobytes",
    KilobytesSecond: "Kilobytes/Second",
    Megabits: "Megabits",
    MegabitsSecond: "Megabits/Second",
    Megabytes: "Megabytes",
    MegabytesSecond: "Megabytes/Second",
    Microseconds: "Microseconds",
    Milliseconds: "Milliseconds",
    None: "None",
    Percent: "Percent",
    Seconds: "Seconds",
    Terabits: "Terabits",
    TerabitsSecond: "Terabits/Second",
    Terabytes: "Terabytes",
    TerabytesSecond: "Terabytes/Second",
};
const QueryStatus = {
    Cancelled: "Cancelled",
    Complete: "Complete",
    Failed: "Failed",
    Running: "Running",
    Scheduled: "Scheduled",
    Timeout: "Timeout",
    Unknown: "Unknown",
};
const PolicyScope = {
    ACCOUNT: "ACCOUNT",
    RESOURCE: "RESOURCE",
};
const Distribution = {
    ByLogStream: "ByLogStream",
    Random: "Random",
};
const EntityRejectionErrorType = {
    ENTITY_SIZE_TOO_LARGE: "EntitySizeTooLarge",
    INVALID_ATTRIBUTES: "InvalidAttributes",
    INVALID_ENTITY: "InvalidEntity",
    INVALID_KEY_ATTRIBUTE: "InvalidKeyAttributes",
    INVALID_TYPE_VALUE: "InvalidTypeValue",
    MISSING_REQUIRED_FIELDS: "MissingRequiredFields",
    UNSUPPORTED_LOG_GROUP_TYPE: "UnsupportedLogGroupType",
};
const EventSource = {
    AWSWAF: "AWSWAF",
    CLOUD_TRAIL: "CloudTrail",
    EKS_AUDIT: "EKSAudit",
    ROUTE53_RESOLVER: "Route53Resolver",
    VPC_FLOW: "VPCFlow",
};
const ExecutionStatus = {
    Complete: "Complete",
    Failed: "Failed",
    InvalidQuery: "InvalidQuery",
    Running: "Running",
    Timeout: "Timeout",
};
const FlattenedElement = {
    FIRST: "first",
    LAST: "last",
};
const OpenSearchResourceStatusType = {
    ACTIVE: "ACTIVE",
    ERROR: "ERROR",
    NOT_FOUND: "NOT_FOUND",
};
const IntegrationStatus = {
    ACTIVE: "ACTIVE",
    FAILED: "FAILED",
    PROVISIONING: "PROVISIONING",
};
const IntegrationType = {
    OPENSEARCH: "OPENSEARCH",
};
const ScheduledQueryDestinationType = {
    S3: "S3",
};
const OCSFVersion = {
    V1_1: "V1.1",
    V1_5: "V1.5",
};
const Type = {
    BOOLEAN: "boolean",
    DOUBLE: "double",
    INTEGER: "integer",
    STRING: "string",
};
const ListAggregateLogGroupSummariesGroupBy = {
    DATA_SOURCE_NAME_AND_TYPE: "DATA_SOURCE_NAME_AND_TYPE",
    DATA_SOURCE_NAME_TYPE_AND_FORMAT: "DATA_SOURCE_NAME_TYPE_AND_FORMAT",
};
const SuppressionState = {
    SUPPRESSED: "SUPPRESSED",
    UNSUPPRESSED: "UNSUPPRESSED",
};
const S3TableIntegrationSourceStatus = {
    ACTIVE: "ACTIVE",
    DATA_SOURCE_DELETE_IN_PROGRESS: "DATA_SOURCE_DELETE_IN_PROGRESS",
    FAILED: "FAILED",
    UNHEALTHY: "UNHEALTHY",
};
const SuppressionUnit = {
    HOURS: "HOURS",
    MINUTES: "MINUTES",
    SECONDS: "SECONDS",
};
const SuppressionType = {
    INFINITE: "INFINITE",
    LIMITED: "LIMITED",
};

exports.$Command = smithyClient.Command;
exports.__Client = smithyClient.Client;
exports.CloudWatchLogsServiceException = CloudWatchLogsServiceException.CloudWatchLogsServiceException;
exports.ActionStatus = ActionStatus;
exports.AnomalyDetectorStatus = AnomalyDetectorStatus;
exports.AssociateKmsKeyCommand = AssociateKmsKeyCommand;
exports.AssociateSourceToS3TableIntegrationCommand = AssociateSourceToS3TableIntegrationCommand;
exports.CancelExportTaskCommand = CancelExportTaskCommand;
exports.CancelImportTaskCommand = CancelImportTaskCommand;
exports.CloudWatchLogs = CloudWatchLogs;
exports.CloudWatchLogsClient = CloudWatchLogsClient;
exports.CreateDeliveryCommand = CreateDeliveryCommand;
exports.CreateExportTaskCommand = CreateExportTaskCommand;
exports.CreateImportTaskCommand = CreateImportTaskCommand;
exports.CreateLogAnomalyDetectorCommand = CreateLogAnomalyDetectorCommand;
exports.CreateLogGroupCommand = CreateLogGroupCommand;
exports.CreateLogStreamCommand = CreateLogStreamCommand;
exports.CreateLookupTableCommand = CreateLookupTableCommand;
exports.CreateScheduledQueryCommand = CreateScheduledQueryCommand;
exports.DataProtectionStatus = DataProtectionStatus;
exports.DeleteAccountPolicyCommand = DeleteAccountPolicyCommand;
exports.DeleteDataProtectionPolicyCommand = DeleteDataProtectionPolicyCommand;
exports.DeleteDeliveryCommand = DeleteDeliveryCommand;
exports.DeleteDeliveryDestinationCommand = DeleteDeliveryDestinationCommand;
exports.DeleteDeliveryDestinationPolicyCommand = DeleteDeliveryDestinationPolicyCommand;
exports.DeleteDeliverySourceCommand = DeleteDeliverySourceCommand;
exports.DeleteDestinationCommand = DeleteDestinationCommand;
exports.DeleteIndexPolicyCommand = DeleteIndexPolicyCommand;
exports.DeleteIntegrationCommand = DeleteIntegrationCommand;
exports.DeleteLogAnomalyDetectorCommand = DeleteLogAnomalyDetectorCommand;
exports.DeleteLogGroupCommand = DeleteLogGroupCommand;
exports.DeleteLogStreamCommand = DeleteLogStreamCommand;
exports.DeleteLookupTableCommand = DeleteLookupTableCommand;
exports.DeleteMetricFilterCommand = DeleteMetricFilterCommand;
exports.DeleteQueryDefinitionCommand = DeleteQueryDefinitionCommand;
exports.DeleteResourcePolicyCommand = DeleteResourcePolicyCommand;
exports.DeleteRetentionPolicyCommand = DeleteRetentionPolicyCommand;
exports.DeleteScheduledQueryCommand = DeleteScheduledQueryCommand;
exports.DeleteSubscriptionFilterCommand = DeleteSubscriptionFilterCommand;
exports.DeleteTransformerCommand = DeleteTransformerCommand;
exports.DeliveryDestinationType = DeliveryDestinationType;
exports.DeliverySourceConfigurationSchemaValueType = DeliverySourceConfigurationSchemaValueType;
exports.DeliverySourceStatus = DeliverySourceStatus;
exports.DeliverySourceStatusReason = DeliverySourceStatusReason;
exports.DescribeAccountPoliciesCommand = DescribeAccountPoliciesCommand;
exports.DescribeConfigurationTemplatesCommand = DescribeConfigurationTemplatesCommand;
exports.DescribeDeliveriesCommand = DescribeDeliveriesCommand;
exports.DescribeDeliveryDestinationsCommand = DescribeDeliveryDestinationsCommand;
exports.DescribeDeliverySourcesCommand = DescribeDeliverySourcesCommand;
exports.DescribeDestinationsCommand = DescribeDestinationsCommand;
exports.DescribeExportTasksCommand = DescribeExportTasksCommand;
exports.DescribeFieldIndexesCommand = DescribeFieldIndexesCommand;
exports.DescribeImportTaskBatchesCommand = DescribeImportTaskBatchesCommand;
exports.DescribeImportTasksCommand = DescribeImportTasksCommand;
exports.DescribeIndexPoliciesCommand = DescribeIndexPoliciesCommand;
exports.DescribeLogGroupsCommand = DescribeLogGroupsCommand;
exports.DescribeLogStreamsCommand = DescribeLogStreamsCommand;
exports.DescribeLookupTablesCommand = DescribeLookupTablesCommand;
exports.DescribeMetricFiltersCommand = DescribeMetricFiltersCommand;
exports.DescribeQueriesCommand = DescribeQueriesCommand;
exports.DescribeQueryDefinitionsCommand = DescribeQueryDefinitionsCommand;
exports.DescribeResourcePoliciesCommand = DescribeResourcePoliciesCommand;
exports.DescribeSubscriptionFiltersCommand = DescribeSubscriptionFiltersCommand;
exports.DisassociateKmsKeyCommand = DisassociateKmsKeyCommand;
exports.DisassociateSourceFromS3TableIntegrationCommand = DisassociateSourceFromS3TableIntegrationCommand;
exports.Distribution = Distribution;
exports.EntityRejectionErrorType = EntityRejectionErrorType;
exports.EvaluationFrequency = EvaluationFrequency;
exports.EventSource = EventSource;
exports.ExecutionStatus = ExecutionStatus;
exports.ExportTaskStatusCode = ExportTaskStatusCode;
exports.FilterLogEventsCommand = FilterLogEventsCommand;
exports.FlattenedElement = FlattenedElement;
exports.GetDataProtectionPolicyCommand = GetDataProtectionPolicyCommand;
exports.GetDeliveryCommand = GetDeliveryCommand;
exports.GetDeliveryDestinationCommand = GetDeliveryDestinationCommand;
exports.GetDeliveryDestinationPolicyCommand = GetDeliveryDestinationPolicyCommand;
exports.GetDeliverySourceCommand = GetDeliverySourceCommand;
exports.GetIntegrationCommand = GetIntegrationCommand;
exports.GetLogAnomalyDetectorCommand = GetLogAnomalyDetectorCommand;
exports.GetLogEventsCommand = GetLogEventsCommand;
exports.GetLogFieldsCommand = GetLogFieldsCommand;
exports.GetLogGroupFieldsCommand = GetLogGroupFieldsCommand;
exports.GetLogObjectCommand = GetLogObjectCommand;
exports.GetLogRecordCommand = GetLogRecordCommand;
exports.GetLookupTableCommand = GetLookupTableCommand;
exports.GetQueryResultsCommand = GetQueryResultsCommand;
exports.GetScheduledQueryCommand = GetScheduledQueryCommand;
exports.GetScheduledQueryHistoryCommand = GetScheduledQueryHistoryCommand;
exports.GetTransformerCommand = GetTransformerCommand;
exports.ImportStatus = ImportStatus;
exports.IndexSource = IndexSource;
exports.IndexType = IndexType;
exports.InheritedProperty = InheritedProperty;
exports.IntegrationStatus = IntegrationStatus;
exports.IntegrationType = IntegrationType;
exports.ListAggregateLogGroupSummariesCommand = ListAggregateLogGroupSummariesCommand;
exports.ListAggregateLogGroupSummariesGroupBy = ListAggregateLogGroupSummariesGroupBy;
exports.ListAnomaliesCommand = ListAnomaliesCommand;
exports.ListIntegrationsCommand = ListIntegrationsCommand;
exports.ListLogAnomalyDetectorsCommand = ListLogAnomalyDetectorsCommand;
exports.ListLogGroupsCommand = ListLogGroupsCommand;
exports.ListLogGroupsForQueryCommand = ListLogGroupsForQueryCommand;
exports.ListScheduledQueriesCommand = ListScheduledQueriesCommand;
exports.ListSourcesForS3TableIntegrationCommand = ListSourcesForS3TableIntegrationCommand;
exports.ListTagsForResourceCommand = ListTagsForResourceCommand;
exports.ListTagsLogGroupCommand = ListTagsLogGroupCommand;
exports.LogGroupClass = LogGroupClass;
exports.OCSFVersion = OCSFVersion;
exports.OpenSearchResourceStatusType = OpenSearchResourceStatusType;
exports.OrderBy = OrderBy;
exports.OutputFormat = OutputFormat;
exports.PolicyScope = PolicyScope;
exports.PolicyType = PolicyType;
exports.PutAccountPolicyCommand = PutAccountPolicyCommand;
exports.PutBearerTokenAuthenticationCommand = PutBearerTokenAuthenticationCommand;
exports.PutDataProtectionPolicyCommand = PutDataProtectionPolicyCommand;
exports.PutDeliveryDestinationCommand = PutDeliveryDestinationCommand;
exports.PutDeliveryDestinationPolicyCommand = PutDeliveryDestinationPolicyCommand;
exports.PutDeliverySourceCommand = PutDeliverySourceCommand;
exports.PutDestinationCommand = PutDestinationCommand;
exports.PutDestinationPolicyCommand = PutDestinationPolicyCommand;
exports.PutIndexPolicyCommand = PutIndexPolicyCommand;
exports.PutIntegrationCommand = PutIntegrationCommand;
exports.PutLogEventsCommand = PutLogEventsCommand;
exports.PutLogGroupDeletionProtectionCommand = PutLogGroupDeletionProtectionCommand;
exports.PutMetricFilterCommand = PutMetricFilterCommand;
exports.PutQueryDefinitionCommand = PutQueryDefinitionCommand;
exports.PutResourcePolicyCommand = PutResourcePolicyCommand;
exports.PutRetentionPolicyCommand = PutRetentionPolicyCommand;
exports.PutSubscriptionFilterCommand = PutSubscriptionFilterCommand;
exports.PutTransformerCommand = PutTransformerCommand;
exports.QueryLanguage = QueryLanguage;
exports.QueryStatus = QueryStatus;
exports.S3TableIntegrationSourceStatus = S3TableIntegrationSourceStatus;
exports.ScheduledQueryDestinationType = ScheduledQueryDestinationType;
exports.ScheduledQueryState = ScheduledQueryState;
exports.Scope = Scope;
exports.StandardUnit = StandardUnit;
exports.StartLiveTailCommand = StartLiveTailCommand;
exports.StartQueryCommand = StartQueryCommand;
exports.State = State;
exports.StopQueryCommand = StopQueryCommand;
exports.SuppressionState = SuppressionState;
exports.SuppressionType = SuppressionType;
exports.SuppressionUnit = SuppressionUnit;
exports.TagLogGroupCommand = TagLogGroupCommand;
exports.TagResourceCommand = TagResourceCommand;
exports.TestMetricFilterCommand = TestMetricFilterCommand;
exports.TestTransformerCommand = TestTransformerCommand;
exports.Type = Type;
exports.UntagLogGroupCommand = UntagLogGroupCommand;
exports.UntagResourceCommand = UntagResourceCommand;
exports.UpdateAnomalyCommand = UpdateAnomalyCommand;
exports.UpdateDeliveryConfigurationCommand = UpdateDeliveryConfigurationCommand;
exports.UpdateLogAnomalyDetectorCommand = UpdateLogAnomalyDetectorCommand;
exports.UpdateLookupTableCommand = UpdateLookupTableCommand;
exports.UpdateScheduledQueryCommand = UpdateScheduledQueryCommand;
exports.paginateDescribeConfigurationTemplates = paginateDescribeConfigurationTemplates;
exports.paginateDescribeDeliveries = paginateDescribeDeliveries;
exports.paginateDescribeDeliveryDestinations = paginateDescribeDeliveryDestinations;
exports.paginateDescribeDeliverySources = paginateDescribeDeliverySources;
exports.paginateDescribeDestinations = paginateDescribeDestinations;
exports.paginateDescribeLogGroups = paginateDescribeLogGroups;
exports.paginateDescribeLogStreams = paginateDescribeLogStreams;
exports.paginateDescribeMetricFilters = paginateDescribeMetricFilters;
exports.paginateDescribeSubscriptionFilters = paginateDescribeSubscriptionFilters;
exports.paginateFilterLogEvents = paginateFilterLogEvents;
exports.paginateGetLogEvents = paginateGetLogEvents;
exports.paginateGetScheduledQueryHistory = paginateGetScheduledQueryHistory;
exports.paginateListAggregateLogGroupSummaries = paginateListAggregateLogGroupSummaries;
exports.paginateListAnomalies = paginateListAnomalies;
exports.paginateListLogAnomalyDetectors = paginateListLogAnomalyDetectors;
exports.paginateListLogGroupsForQuery = paginateListLogGroupsForQuery;
exports.paginateListScheduledQueries = paginateListScheduledQueries;
exports.paginateListSourcesForS3TableIntegration = paginateListSourcesForS3TableIntegration;
Object.prototype.hasOwnProperty.call(schemas_0, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: schemas_0['__proto__']
    });

Object.keys(schemas_0).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = schemas_0[k];
});
Object.prototype.hasOwnProperty.call(errors, '__proto__') &&
    !Object.prototype.hasOwnProperty.call(exports, '__proto__') &&
    Object.defineProperty(exports, '__proto__', {
        enumerable: true,
        value: errors['__proto__']
    });

Object.keys(errors).forEach(function (k) {
    if (k !== 'default' && !Object.prototype.hasOwnProperty.call(exports, k)) exports[k] = errors[k];
});
